import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { isNil, languages } from "lopermut"
import { useMarketingChannelOptions } from "modules/customers/hooks/useMarketingChannelOptions"
import { useTagsQuery } from "modules/customers/hooks/useTagsQuery"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { ExportTable } from "modules/shared/components/ExportTable"
import { useCountries } from "modules/shared/hooks/useCountries"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route as CustomersListRoute } from "routes/_base/_auth/customers/list/route"

export function CustomersFilter() {
  const { t } = useTranslation()
  const search = CustomersListRoute.useSearch()
  const navigate = CustomersListRoute.useNavigate()
  const labels = useFilterBarLabels()

  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { data: tags = [] } = useTagsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const { options: countryOptions } = useCountries()
  const { data: integrationsStatus } = useIntegrationsStatusQuery()
  const marketingChannelOptions = useMarketingChannelOptions()

  function onChangeFilter<T extends keyof NonNullable<typeof search.filterBy>>(
    key: T,
    value: unknown,
  ) {
    const newFilterBy = { ...search.filterBy }

    if (isNil(value)) {
      delete newFilterBy[key]
    } else {
      newFilterBy[key] = value as NonNullable<typeof search.filterBy>[T]
    }

    navigate({
      search: {
        ...search,
        page: 1,
        filterBy: newFilterBy,
      },
    })
  }

  function onClearFilters() {
    navigate({
      search: {
        ...search,
        filterBy: undefined,
        query: undefined,
        page: 1,
      },
    })
  }

  const subscribedToFields: FilterBarField[] = integrationsStatus?.shopify
    ? [
        {
          key: "subscribedTo",
          type: "multiSelect",
          label: t("customers.filters.subscribedTo"),
          value: search.filterBy?.subscribedTo,
          onChange: (value) => onChangeFilter("subscribedTo", value),
          options: marketingChannelOptions,
        },
      ]
    : []

  const fields: FilterBarField[] = [
    {
      key: "segmentIds",
      type: "multiSelect",
      label: t("customers.filters.segment"),
      value: search.filterBy?.segmentIds,
      onChange: (value) => onChangeFilter("segmentIds", value),
      options: segmentGroups.flatMap((segmentGroup) =>
        segmentGroup.segments.map((segment) => ({
          value: segment.id,
          label: getSegmentTranslation(segment.name, segmentGroup),
          group: getSegmentGroupTranslation(segmentGroup) || segmentGroup.name,
        })),
      ),
    },
    {
      key: "tagIds",
      type: "multiSelect",
      label: t("customers.tags.title"),
      value: search.filterBy?.tagIds,
      onChange: (value) => onChangeFilter("tagIds", value),
      options: tags.map((tag) => ({ value: tag.id, label: tag.name })),
    },
    ...subscribedToFields,
    {
      key: "country",
      type: "multiSelect",
      label: t("customers.filters.country"),
      value: search.filterBy?.country,
      onChange: (value) => onChangeFilter("country", value),
      options: countryOptions,
    },
    {
      key: "locale",
      type: "multiSelect",
      label: t("customers.filters.locale"),
      value: search.filterBy?.locale,
      onChange: (value) => onChangeFilter("locale", value),
      options: languages,
    },
    {
      key: "hasEmail",
      type: "select",
      label: t("customers.filters.hasEmail.title"),
      value: mapBooleanFilterToLabel(search.filterBy?.hasEmail),
      onChange: (value) => onChangeFilter("hasEmail", isNil(value) ? value : value === "true"),
      options: [
        { value: "true", label: t("customers.filters.hasEmail.true") },
        { value: "false", label: t("customers.filters.hasEmail.false") },
      ],
    },
    {
      key: "hasPhone",
      type: "select",
      label: t("customers.filters.hasPhone.title"),
      value: mapBooleanFilterToLabel(search.filterBy?.hasPhone),
      onChange: (value) => onChangeFilter("hasPhone", isNil(value) ? value : value === "true"),
      options: [
        { value: "true", label: t("customers.filters.hasPhone.true") },
        { value: "false", label: t("customers.filters.hasPhone.false") },
      ],
    },
    {
      key: "hasOptedOut",
      type: "select",
      label: t("customers.filters.hasOptedOut.title"),
      value: mapBooleanFilterToLabel(search.filterBy?.hasOptedOut),
      onChange: (value) => onChangeFilter("hasOptedOut", isNil(value) ? value : value === "true"),
      options: [
        { value: "true", label: t("customers.filters.hasOptedOut.true") },
        { value: "false", label: t("customers.filters.hasOptedOut.false") },
      ],
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("customers.table.search")}
      trailing={<ExportTable source="customers" query={search} />}
    />
  )
}

function mapBooleanFilterToLabel(value: boolean | undefined) {
  if (isNil(value)) return undefined
  return value ? "true" : "false"
}
